<script lang="ts">
  import { Spinner, EditFilled, LabelFilled, Cross, Check } from "$components/+blocks/OVIcon";
  import { createEventDispatcher } from "svelte";
  import { alerts } from "app/stores/alert";
  import IconButton from "$components/+blocks/IconButton/IconButton.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import { stripLocationPrefix, addLocationPrefix } from "./utils";

  export let tags: string[] = [];

  const dispatch = createEventDispatcher();

  let inputValue: string = "";
  let isEditing: boolean = false;
  let inputElement: HTMLInputElement;
  let isSaving: boolean = false;

  // initialize input value when tags prop changes (only when not currently editing)
  $: {
    if (!isEditing) {
      inputValue = stripLocationPrefix(tags).join(", ");
    }
  }

  function handleTagsInput(event: Event) {
    const target = event.target as HTMLInputElement;
    inputValue = target?.value || "";
  }

  function startEditing() {
    isEditing = true;
    setTimeout(() => {
      inputElement?.focus();
    }, 0);
  }

  function cancelEdit() {
    isEditing = false;
    inputValue = stripLocationPrefix(tags).join(", ");
  }

  function saveTags() {
    const newTags = inputValue
      ? inputValue
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag)
      : [];

    // add location- prefix before saving to backend
    const tagsWithPrefix = addLocationPrefix(newTags);
    isSaving = true;

    setTimeout(() => {
      dispatch("tagsChanged", tagsWithPrefix);
      const tagsString = stripLocationPrefix(tagsWithPrefix).join(", ");
      alerts.success("Tags updated", {
        description: tagsString ? `Tags updated to ${tagsString}` : "Tags updated to empty",
      });
      isEditing = false;
      isSaving = false;
    }, 500);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      saveTags();
    }
  }
</script>

<div
  class="flex h-[42px] w-full items-center justify-between gap-2.5 overflow-hidden rounded-[10px] border-[0.5px]
  border-black/20 bg-transparent py-1.5 pl-3 pr-1.5 text-[13px] text-sm transition-colors focus-within:border-black/40
  md:text-[13px] dark:border-white/10 dark:focus-within:border-white/20"
>
  <OVIcon
    icon={LabelFilled}
    class="shrink-0 text-neutral-400 dark:text-neutral-500"
    size="size-3.5"
  />

  <input
    bind:this={inputElement}
    name="tags"
    type="text"
    placeholder="Add tags..."
    value={inputValue}
    disabled={!isEditing}
    on:input={handleTagsInput}
    on:keydown={handleKeyDown}
    class="min-w-0 flex-1 truncate border-0 p-0 text-[13px] placeholder:text-neutral-400 focus:ring-0
    dark:bg-transparent dark:text-neutral-100 dark:placeholder:text-neutral-600"
    class:text-neutral-500={!isEditing}
    class:dark:text-neutral-400={!isEditing}
  />

  {#if !isEditing}
    <div class="shrink-0">
      <IconButton
        icon={EditFilled}
        on:click={startEditing}
        a11yLabel="Edit tags"
        variant="tertiary"
      />
    </div>
  {:else}
    <div class="flex shrink-0 items-center gap-1">
      {#if isSaving}
        <span class="ml-1 flex size-[28px] items-center justify-center p-1">
          <OVIcon icon={Spinner} class="animate-spin text-neutral-400 dark:text-neutral-500" />
        </span>
      {:else}
        <IconButton icon={Cross} on:click={cancelEdit} a11yLabel="Cancel" variant="tertiary" />
        <IconButton icon={Check} on:click={saveTags} a11yLabel="Update tags" variant="system" />
      {/if}
    </div>
  {/if}
</div>
